import { formatDuration } from '../lib/format';
import { hasPlayableAsset } from '../lib/playback';
import { castAvatarUrl } from '../lib/cast-avatar';
import { RatingChips } from './RatingChips';
import type { AudioTrack, MovieDetail } from '../lib/types';
import { useI18n } from '../lib/use-i18n';

function backgroundStyle(movie: MovieDetail): React.CSSProperties {
  const image = movie.backdrop;
  return image ? { backgroundImage: `url("${image}")` } : {};
}

export function MovieDetailOverlay({ movie, inLibrary, inWatchlist, loading, onClose, onPlay, onToggleWatchlist, onRequest }: {
  movie: MovieDetail;
  inLibrary: boolean;
  inWatchlist: boolean;
  loading: boolean;
  onClose: () => void;
  onPlay: () => void;
  onToggleWatchlist: () => void;
  onRequest: () => void;
}) {
  const { locale, localeTag, t } = useI18n();
  const countries = movie.countries?.map((country) => typeof country === 'string' ? country : country.name).join(', ');
  const genres = movie.genres?.join(' · ') ?? movie.metadata?.genres?.join(' · ');
  const duration = formatDuration(movie.durationSec ?? (movie.runtime ? movie.runtime * 60 : null), locale);
  const playable = hasPlayableAsset(movie);
  const hasMeta = Boolean(movie.year || duration !== '—' || (movie.audio?.length ?? 0) > 1);
  const hasDetailsFacts = Boolean(genres || countries || movie.directors?.length);
  const hasFacts = hasDetailsFacts || Boolean(movie.audio?.length);
  const audioTracks = movie.audio?.map((track) => ({
    ...track,
    displayLabel: audioTrackLabel(track, localeTag, t('original')),
  })) ?? [];
  const status = playable
    ? t('inLibrary')
    : !inLibrary
      ? t('availableToAdd')
      : movie.availability.status === 'failed'
        ? t('temporarilyUnavailable')
        : t('availableSoon');

  return (
    <div className="detail-overlay" role="dialog" aria-modal="true" aria-label={movie.title}>
      <div className="detail-backdrop" style={backgroundStyle(movie)} />
      <div className="detail-gradient" />
      <button className="detail-back" data-focusable="true" type="button" onClick={onClose} aria-label={t('goBack')}>
        <svg aria-hidden="true" viewBox="0 0 24 24"><path d="m14 6-6 6 6 6M8 12h10" /></svg>
        <span>{t('backToCatalog')}</span>
      </button>
      <button className="detail-close" data-focusable="true" type="button" onClick={onClose} aria-label={t('close')}>
        <svg aria-hidden="true" viewBox="0 0 24 24"><path d="M6 6 18 18M18 6 6 18" /></svg>
      </button>
      <div className="detail-content">
        <div className="detail-layout">
          <section className="detail-primary">
            <span className="detail-status"><i />{status}</span>
            <h1>{movie.title}</h1>
            {movie.tagline && <p className="detail-tagline">{movie.tagline}</p>}
            {hasMeta && <div className="detail-meta">
              {movie.year && <span>{movie.year}</span>}
              {duration !== '—' && <span>{duration}</span>}
              {movie.audio?.length > 1 && <span>{t('audioTracksCount', { count: movie.audio.length })}</span>}
            </div>}
            <RatingChips ratings={movie.ratings} />
            <p className={`detail-description ${movie.description ? '' : 'empty'}`}>{movie.description || (loading ? t('loadingDetails') : t('noDescription'))}</p>
            <div className="detail-actions">
              {(playable
                ? <button className="primary-action" data-focusable="true" data-autofocus="true" onClick={onPlay}>▶ {t('watch')}</button>
                : !inLibrary && <button className="primary-action" data-focusable="true" data-autofocus="true" onClick={onRequest} disabled={loading}>{loading ? t('pleaseWait') : `＋ ${t('addToLibrary')}`}</button>)}
              <button className="secondary-action" data-focusable="true" onClick={onToggleWatchlist}>{inWatchlist ? `✓ ${t('saved')}` : `＋ ${t('save')}`}</button>
              <button className="secondary-action detail-catalog-action" data-focusable="true" type="button" onClick={onClose} aria-label={t('backToCatalog')}>
                <svg aria-hidden="true" viewBox="0 0 24 24"><path d="m14 6-6 6 6 6M8 12h10" /></svg>
                {t('allMovies')}
              </button>
            </div>
          </section>
          {hasFacts && <aside className="detail-info-card">
            {audioTracks.length ? <section className="detail-audio" aria-label={t('audio')}>
              <div className="detail-audio-heading">
                <svg aria-hidden="true" viewBox="0 0 24 24"><path d="M5 10v4M9 7v10M13 4v16M17 8v8M21 10v4" /></svg>
                <span><small>{t('audio')}</small><b>{audioTracks.length === 1 ? audioTracks[0].displayLabel : t('audioTracksCount', { count: audioTracks.length })}</b></span>
              </div>
              <div className="detail-audio-tracks">
                {audioTracks.map((track, index) => <span className={track.default ? 'default' : ''} key={`${track.language}-${track.label}-${index}`}>
                  <i aria-hidden="true">{String(index + 1).padStart(2, '0')}</i>
                  <b>{track.displayLabel}</b>
                  <span className="detail-track-wave" aria-hidden="true"><i /><i /><i /></span>
                </span>)}
              </div>
            </section> : null}
            {hasDetailsFacts && <dl className="detail-facts">
                {genres && <div><dt>{t('genres')}</dt><dd>{genres}</dd></div>}
                {countries && <div><dt>{t('countries')}</dt><dd>{countries}</dd></div>}
                {movie.directors?.length ? <div><dt>{t('director')}</dt><dd>{movie.directors.map((person) => person.name).join(', ')}</dd></div> : null}
              </dl>}
          </aside>}
        </div>
        {movie.actors?.length ? (
          <section className="cast"><h2>{t('cast')}</h2><div>{movie.actors.slice(0, 8).map((person) => {
            const avatar = castAvatarUrl(person.profile);
            return <article key={person.name}><span className="cast-avatar" style={avatar ? { backgroundImage: `url("${avatar}")` } : undefined}>{avatar ? '' : person.name.slice(0, 1)}</span><b>{person.name}</b><small>{person.character}</small></article>;
          })}</div></section>
        ) : null}
      </div>
    </div>
  );
}

function audioTrackLabel(track: AudioTrack, localeTag: string, original: string): string {
  const label = track.label?.trim();
  const language = track.language?.trim().toLowerCase();
  if (label && !['und', 'unk', 'unknown'].includes(label.toLowerCase())) return label;
  if (!language || ['und', 'unk', 'unknown'].includes(language)) return original;
  try { return new Intl.DisplayNames([localeTag], { type: 'language' }).of(language) ?? language.toUpperCase(); }
  catch { return language.toUpperCase(); }
}
